<template>
  <div
    v-if="isEditing && hasChild === Types.ChildrenStateAnswer.HasChildren"
    :class="$style.children"
  >
    <ul>
      <li v-for="(child, index) in modelValue" :key="child.key">
        <p>{{ index + 1 }}人目</p>
        <p>性別:</p>
        <DolphinSelector
          :class="$style.children__selector"
          :options="childrenGenderOptions"
          v-model="child.gender"
          small
          round
        />
        <p>誕生日:</p>
        <DolphinTextInput
          type="date"
          :modelValue="child.birthday"
          @update:modelValue="value => updateChildrenBirthday(value, index)"
        />
        <ButtonClose v-if="index > 0" @click="removeChild(index)" />
      </li>
    </ul>
    <button :class="$style.children__append" @click="appendChild">
      ＋追加
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { nanoid } from 'nanoid'

import * as Types from '@/types/schema.generated'
import type { Option, OptionValueType } from '@/types/app'

import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import ButtonClose from '@/components/atoms/ButtonClose.vue'

import { GenderToString } from '@/models/gender/GenderToString'
import { parseDateString } from '@/libs/utilities/gql-scalar'

type Child = {
  gender: Types.Gender
  birthday: DateString
}

type FormChild = Child & {
  key: string
}

const props = withDefaults(
  defineProps<{
    modelValue?: FormChild[]
    hasChild?: Types.ChildrenStateAnswer
    isEditing?: boolean
  }>(),
  {
    modelValue: undefined,
    hasChild: Types.ChildrenStateAnswer.Unanswered,
    isEditing: false,
  }
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: FormChild[]): void
  (event: 'handleInvalidKeys', value: string[]): void
}>()

const childrenGenderOptions: Option<OptionValueType>[] = [
  {
    value: Types.Gender.Male,
    text: GenderToString[Types.Gender.Male],
  },
  {
    value: Types.Gender.Female,
    text: GenderToString[Types.Gender.Female],
  },
  {
    value: Types.Gender.Other,
    text: GenderToString[Types.Gender.Other],
  },
  {
    value: Types.Gender.NoAnswer,
    text: GenderToString[Types.Gender.NoAnswer],
  },
]

const childrenBirthdayInvalidKeys = ref<string[]>([])
const addChildrenBirthdayInvalidKey = (key: string) => {
  if (childrenBirthdayInvalidKeys.value.includes(key)) {
    return
  }
  childrenBirthdayInvalidKeys.value = [
    ...childrenBirthdayInvalidKeys.value,
    key,
  ]

  emit('handleInvalidKeys', childrenBirthdayInvalidKeys.value)
}
const removeChildrenBirthdayInvalidKey = (key: string) => {
  childrenBirthdayInvalidKeys.value = childrenBirthdayInvalidKeys.value.filter(
    value => value !== key
  )
}

const updateChildrenBirthday = (value: string | number, index: number) => {
  if (!props.modelValue) {
    return
  }

  const key = props.modelValue[index].key
  try {
    emit('update:modelValue', [
      ...props.modelValue.slice(0, index),
      {
        ...props.modelValue[index],
        birthday: parseDateString(new Date(value)),
      },
      ...props.modelValue.slice(index + 1),
    ])
    removeChildrenBirthdayInvalidKey(key)
  } catch {
    // parse不可能な場合、input要素のDOM上の状態とVueの値は一時的に切り離される
    // 不一致であること(=== invalidな値であること)を記録する
    addChildrenBirthdayInvalidKey(key)
  }
}

const getNewChild = (): FormChild => ({
  key: nanoid(),
  gender: Types.Gender.NoAnswer,
  birthday: parseDateString(new Date()),
})

const appendChild = () => {
  if (!props.modelValue) {
    return
  }

  emit('update:modelValue', [...props.modelValue, getNewChild()])
}

const removeChild = (index: number) => {
  if (!props.modelValue) {
    return
  }

  removeChildrenBirthdayInvalidKey(props.modelValue[index].key)
  emit('update:modelValue', [
    ...props.modelValue.slice(0, index),
    ...props.modelValue.slice(index + 1),
  ])
}

watch(
  () => props.hasChild,
  value => {
    if (value !== Types.ChildrenStateAnswer.HasChildren) {
      emit('update:modelValue', [])
      return
    }

    if (value === Types.ChildrenStateAnswer.HasChildren) {
      appendChild()
    }
  }
)
</script>

<style lang="scss" module>
.children {
  ul {
    margin-top: 10px;

    li {
      display: flex;
      align-items: center;

      & > *:nth-child(n + 2) {
        margin-left: 8px;
      }

      &:nth-child(n + 2) {
        margin-top: 10px;
      }
    }
  }

  &__selector {
    width: 122px;
  }

  &__append {
    color: $color-text-and-object-accent-primary;
    line-height: 2;
  }
}
</style>
